var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1378 — one value of a repeatable subfield, drawn as if the subfield held
 * only that value (variant C of CZNA-1031).
 *
 * The row is an input plus, when the subfield is backed by a vocabulary, the
 * suggestions under it (CZNA-1349), "+ Přidat nový termín" (CZNA-1348) and a
 * magnifier that opens the register in SINGLE-select mode with the row's text as
 * its search (CZNA-1350). Whatever is picked lands in THIS row and nowhere else.
 *
 * What the row does not have is a draft. Every keystroke goes straight into the
 * row's entry in `values[]`, so text on screen is text in the record — the MARC
 * preview reads it at once and a save stores it. That is the whole point of the
 * redesign: CZNA-1030 stopped the loss of a typed-but-not-confirmed value, but the
 * cataloger still could not see that it was not a value yet. Here there is no
 * such state to see. Blank rows are dropped by the codec (`genericFormToProposalData`
 * trims and filters), so an empty row never reaches the record either.
 *
 * Editing the text of a LINKED row drops its `sourceAuthorityId`: what the row now
 * says is no longer the term it pointed at, and keeping the FK would claim it is.
 * The "ze slovníku" badge goes with it, so the change is visible.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { IconButton } from '@folio/stripes/components';
import useRecordPickerOptions from '../../../hooks/useRecordPickerOptions';
import { typedAnchor } from '../../../utils/pickerRegister';
import VocabularyForm from '../../VocabularyForm';
import RecordPickerModal from './RecordPickerModal';
import { subfieldActionsStyle, subfieldInputStyle } from './subfieldStyles';
/** The box the suggestion list is positioned against. */
var inputBoxStyle = { position: 'relative', flex: 1, minWidth: 0 };
/** The gap of a single-value subfield row, so the actions slot lands where theirs does. */
var rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
};
/**
 * The same input as every single-value subfield (CZNA-1572), not a Stripes TextField:
 * that one brings its own height, border and background, and 670 $b stood out under $a.
 */
var inputStyle = __assign(__assign({}, subfieldInputStyle), { width: '100%' });
/** Room for the badge at the input's right end; the box itself keeps its width. */
var linkedInputStyle = __assign(__assign({}, inputStyle), { padding: '6px 88px 6px 10px' });
/**
 * The "ze slovníku" badge, at the right end INSIDE the input box (CZNA-1572): beside
 * it, the badge made a linked row's input shorter than every other input of the form.
 * The input reserves the room ({@link linkedInputStyle}), so a long value ends before
 * the badge instead of running under it. Muted and small: it is a property of the
 * value, not an action.
 */
var linkedBadgeStyle = {
    position: 'absolute',
    top: '50%',
    right: '6px',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    padding: '1px 6px',
    borderRadius: '8px',
    backgroundColor: '#e0ecf6',
    color: '#1960a4',
    fontSize: '11px',
    lineHeight: 1.4,
};
var suggestionListStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 10,
    minWidth: '100%',
    maxHeight: '240px',
    overflowY: 'auto',
    margin: '2px 0 0',
    padding: '2px 0',
    listStyle: 'none',
    backgroundColor: '#fff',
    border: '1px solid #bbb',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
};
var suggestionStyle = {
    padding: '4px 10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '13px',
};
var activeSuggestionStyle = __assign(__assign({}, suggestionStyle), { backgroundColor: '#e0ecf6', color: '#1960a4' });
/** CZNA-1348 — the create row, set apart as the single-value picker sets it apart. */
var addTermRowStyle = {
    fontWeight: 600,
    color: '#1960a4',
    borderBottom: '1px solid #e0e0e0',
};
/**
 * CZNA-1349 — how many suggestions the box offers at most. The register window is
 * 50 rows; a list that long under a text box is a browse, and browsing is what the
 * magnifier is for.
 */
var MAX_SUGGESTIONS = 10;
/** `activeSuggestion` value for the create row — see the state's comment. */
var ADD_TERM_ROW = -2;
/**
 * Case-insensitive, trimmed comparison — the one the vocabulary itself uses (the
 * derive idempotence guard and the rematch SQL compare with `LOWER()`), and
 * `toLowerCase` rather than the locale variant so a Turkish-locale browser does not
 * disagree with PostgreSQL about which strings are the same term.
 */
var sameValue = function (a, b) {
    return (a !== null && a !== void 0 ? a : '').trim().toLowerCase() === (b !== null && b !== void 0 ? b : '').trim().toLowerCase();
};
/**
 * CZNA-1349 — the register rows the typed text is a prefix of. The window is opened
 * AT the typed text and runs on past it in register order (CZNA-1033), so its tail
 * holds terms that merely sort after it; offering those would read as "these
 * match". Compared on the row the term was FOUND under — a variant row inserts its
 * preferred heading and says so in its label.
 */
var suggestionsFor = function (options, text, linkedIds) {
    var prefix = typedAnchor(text);
    if (!prefix)
        return [];
    return options
        .filter(function (o) { return !o.isProposal && !linkedIds.has(o.value); })
        .filter(function (o) { var _a; return ((_a = o.found) !== null && _a !== void 0 ? _a : o.heading).toLocaleLowerCase('cs').startsWith(prefix); })
        .slice(0, MAX_SUGGESTIONS);
};
var RepeatableValueRow = function (_a) {
    var _b;
    var entry = _a.entry, index = _a.index, onChange = _a.onChange, onRemove = _a.onRemove, removable = _a.removable, recordType = _a.recordType, supportsAddTerm = _a.supportsAddTerm, otherLinkedIds = _a.otherLinkedIds, labelId = _a.labelId, rowId = _a.rowId, label = _a.label, placeholder = _a.placeholder;
    var intl = useIntl();
    var hasVocabulary = recordType !== '';
    var text = (_b = entry.value) !== null && _b !== void 0 ? _b : '';
    var linked = Boolean(entry.sourceAuthorityId);
    // An empty record type keeps the hook's query disabled, so a free-text row never
    // reads the register.
    var _c = useRecordPickerOptions({ recordType: recordType }), registerRows = _c.dataOptions, handleFilter = _c.handleFilter, windowAnchor = _c.windowAnchor, registerFailed = _c.failed;
    var _d = useState(false), pickerOpen = _d[0], setPickerOpen = _d[1];
    // The create form's seed while it is open, null while it is not.
    var _e = useState(null), termSeed = _e[0], setTermSeed = _e[1];
    // Closed by Escape, blur and a pick; reopened by the next keystroke.
    var _f = useState(false), suggestionsOpen = _f[0], setSuggestionsOpen = _f[1];
    // -1 = nothing highlighted; ADD_TERM_ROW = the create row; n >= 0 = the n-th term.
    // It names WHAT is highlighted, not a position: a late register answer can put the
    // create row above the terms, and a position would then point at another row.
    var _g = useState(-1), activeSuggestion = _g[0], setActiveSuggestion = _g[1];
    var listId = "".concat(rowId, "-suggestions");
    var suggestions = hasVocabulary && suggestionsOpen
        ? suggestionsFor(registerRows, text, otherLinkedIds)
        : [];
    // CZNA-1348 — offer to create the typed text when the register does not hold it,
    // and only once the window opened AT this text has answered: a failed read, or the
    // previous keystroke's window held through the debounce, says nothing about whether
    // the term exists. A linked row already IS a term, so it is offered nothing.
    var typed = text.trim();
    var offerAddTerm = supportsAddTerm
        && suggestionsOpen
        && !linked
        && Boolean(typed)
        && windowAnchor === typedAnchor(typed)
        && !registerFailed
        && !registerRows.some(function (o) { return sameValue(o.heading, typed) || sameValue(o.found, typed); });
    // The create row comes first (TASK-145), so option 0 is it when offered.
    var termOffset = offerAddTerm ? 1 : 0;
    var optionCount = termOffset + suggestions.length;
    var highlighted = -1;
    if (activeSuggestion === ADD_TERM_ROW) {
        highlighted = offerAddTerm ? 0 : -1;
    }
    else if (activeSuggestion >= 0 && activeSuggestion < suggestions.length) {
        highlighted = activeSuggestion + termOffset;
    }
    var highlightOption = function (idx) {
        setActiveSuggestion(offerAddTerm && idx === 0 ? ADD_TERM_ROW : idx - termOffset);
    };
    var closeSuggestions = function () {
        setSuggestionsOpen(false);
        setActiveSuggestion(-1);
    };
    // Every keystroke is the value. An edit of a linked row is no longer that term,
    // so the link goes (Mantinely: the cataloger's spelling, never a stale FK).
    var changeText = function (next) {
        onChange({ value: next });
        if (hasVocabulary) {
            setSuggestionsOpen(true);
            setActiveSuggestion(-1);
            handleFilter(next);
        }
    };
    // A pick writes the term's preferred heading into THIS row, with its FK.
    var linkTerm = function (id, heading) {
        onChange({ value: heading, sourceAuthorityId: id });
        closeSuggestions();
    };
    var startAddTerm = function () {
        setTermSeed(typed);
        closeSuggestions();
    };
    var chooseOption = function (idx) {
        if (offerAddTerm && idx === 0)
            startAddTerm();
        else {
            var option = suggestions[idx - termOffset];
            linkTerm(option.value, option.heading);
        }
    };
    var handleTermCreated = function (created) {
        setTermSeed(null);
        // Created from the register modal, which stays open behind the form: the new
        // term is the answer to that modal too.
        setPickerOpen(false);
        if ((created === null || created === void 0 ? void 0 : created.id) && (created === null || created === void 0 ? void 0 : created.name))
            linkTerm(created.id, created.name);
    };
    return (React.createElement("div", { style: rowStyle, "data-test-multi-row": index },
        React.createElement("div", { style: inputBoxStyle },
            React.createElement("input", { type: "text", style: linked ? linkedInputStyle : inputStyle, value: text, onChange: function (e) { return changeText(e.target.value); }, onKeyDown: function (e) {
                    if (e.key === 'ArrowDown' && optionCount > 0) {
                        e.preventDefault();
                        highlightOption((highlighted + 1) % optionCount);
                    }
                    else if (e.key === 'ArrowUp' && optionCount > 0) {
                        e.preventDefault();
                        highlightOption(highlighted <= 0 ? optionCount - 1 : highlighted - 1);
                    }
                    else if (e.key === 'Escape') {
                        // Also before the debounced register read has answered: the list must
                        // not open later against the dismissal. Swallowed only when there was
                        // a list to close.
                        if (optionCount > 0)
                            e.preventDefault();
                        closeSuggestions();
                    }
                    else if (e.key === 'Enter') {
                        // Never submits the surrounding form. With a suggestion highlighted it
                        // takes it; otherwise there is nothing to confirm — the text already is
                        // the value.
                        e.preventDefault();
                        if (highlighted >= 0)
                            chooseOption(highlighted);
                    }
                }, onBlur: hasVocabulary ? closeSuggestions : undefined, placeholder: placeholder, "aria-labelledby": labelId, role: hasVocabulary ? 'combobox' : undefined, "aria-autocomplete": hasVocabulary ? 'list' : undefined, "aria-expanded": hasVocabulary ? optionCount > 0 : undefined, "aria-controls": hasVocabulary ? listId : undefined, "aria-activedescendant": highlighted >= 0 ? "".concat(listId, "-").concat(highlighted) : undefined, autoComplete: "off", "data-test-multi-row-input": true }),
            linked && (React.createElement("span", { style: linkedBadgeStyle, "data-test-multi-row-linked": true }, intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.linked' }))),
            optionCount > 0 && (React.createElement("ul", { id: listId, role: "listbox", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.suggestions' }), style: suggestionListStyle, "data-test-multi-suggestions": true },
                offerAddTerm && (
                // Same keyboard path as the terms below.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                React.createElement("li", { id: "".concat(listId, "-0"), role: "option", "aria-selected": highlighted === 0, style: __assign(__assign({}, (highlighted === 0 ? activeSuggestionStyle : suggestionStyle)), addTermRowStyle), onMouseDown: function (e) { return e.preventDefault(); }, onClick: startAddTerm, "data-test-multi-add-term": true }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.addNewTerm' }, { text: typed }))),
                suggestions.map(function (option, i) {
                    var idx = i + termOffset;
                    return (
                    // The keyboard path is the combobox's: focus stays in the input,
                    // which walks the options (aria-activedescendant).
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                    React.createElement("li", { key: option.value, id: "".concat(listId, "-").concat(idx), role: "option", "aria-selected": idx === highlighted, style: idx === highlighted ? activeSuggestionStyle : suggestionStyle, 
                        // Keep focus in the input so its blur does not close the list
                        // before the click lands.
                        onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return linkTerm(option.value, option.heading); }, "data-test-multi-suggestion": option.value }, option.label));
                })))),
        React.createElement("span", { style: subfieldActionsStyle },
            React.createElement("span", { style: removable ? undefined : { visibility: 'hidden' } },
                React.createElement(IconButton, { icon: "times", onClick: onRemove, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.removeRow' }, { label: label, n: index + 1 }), "data-test-multi-row-remove": true })),
            hasVocabulary && (React.createElement(IconButton, { icon: "search", onClick: function () { return setPickerOpen(true); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.browseRow' }, { label: label, n: index + 1 }), "data-test-multi-row-pick": true }))),
        pickerOpen && (React.createElement(RecordPickerModal, { open: pickerOpen, onClose: function () { return setPickerOpen(false); }, recordType: recordType, 
            // CZNA-1350 — what the row says is what the cataloger was looking for.
            initialSearchTerm: linked ? undefined : typed, onSelect: function (id, heading) {
                setPickerOpen(false);
                linkTerm(id, heading);
            }, 
            // CZNA-1348 — the same create the suggestions offer, under the same gate.
            allowAddNew: supportsAddTerm, onAddNew: function (seed) { return setTermSeed(seed.trim()); } })),
        termSeed !== null && (React.createElement(VocabularyForm, { defaultType: recordType, lockType: true, defaultName: termSeed, 
            // Cancelled: the row keeps what was typed — it already is the value.
            onClose: function () { return setTermSeed(null); }, onSuccess: handleTermCreated }))));
};
export default RepeatableValueRow;
